import type { SubmissionValues } from "@/lib/validation/submission";

export const SUBMIT_CONTEXT_STORAGE_KEY = "costdoc_submit_context";

export type SubmitContext = {
  returnTo: string;
  verticalSlug?: string;
  citySlug?: string;
  procedureSlug?: string;
  proposedProcedureName?: string;
  providerSlug?: string;
  providerName?: string;
  suburb?: string;
};

function cleanValue(value?: string) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function normalizeSubmitContext(context: SubmitContext): SubmitContext | null {
  const returnTo = cleanValue(context.returnTo);
  if (!returnTo) {
    return null;
  }

  return {
    returnTo,
    verticalSlug: cleanValue(context.verticalSlug),
    citySlug: cleanValue(context.citySlug),
    procedureSlug: cleanValue(context.procedureSlug),
    proposedProcedureName: cleanValue(context.proposedProcedureName),
    providerSlug: cleanValue(context.providerSlug),
    providerName: cleanValue(context.providerName),
    suburb: cleanValue(context.suburb),
  };
}

export function getSubmitContextInitialValues(
  context?: SubmitContext | null,
): Partial<SubmissionValues> {
  const normalized = context ? normalizeSubmitContext(context) : null;
  if (!normalized) {
    return {};
  }

  const initialValues: Partial<SubmissionValues> = {
    returnTo: normalized.returnTo,
  };

  if (normalized.verticalSlug) {
    initialValues.verticalSlug = normalized.verticalSlug;
  }

  if (normalized.citySlug) {
    initialValues.citySlug = normalized.citySlug;
  }

  if (normalized.procedureSlug) {
    initialValues.procedureSlug = normalized.procedureSlug;
  }

  if (normalized.proposedProcedureName) {
    initialValues.proposedProcedureName = normalized.proposedProcedureName;
  }

  if (normalized.providerName) {
    initialValues.providerName = normalized.providerName;
  }

  if (normalized.suburb) {
    initialValues.suburb = normalized.suburb;
  }

  return initialValues;
}

export function buildSubmitHref(context: SubmitContext) {
  const normalized = normalizeSubmitContext(context);
  if (!normalized) {
    return "/submit";
  }

  const params = new URLSearchParams();
  params.set("returnTo", normalized.returnTo);

  if (normalized.verticalSlug) {
    params.set("verticalSlug", normalized.verticalSlug);
  }

  if (normalized.citySlug) {
    params.set("citySlug", normalized.citySlug);
  }

  if (normalized.procedureSlug) {
    params.set("procedureSlug", normalized.procedureSlug);
  }

  if (normalized.proposedProcedureName) {
    params.set("proposedProcedureName", normalized.proposedProcedureName);
  }

  return `/submit?${params.toString()}`;
}

export function writeSubmitContext(context: SubmitContext) {
  if (typeof window === "undefined") {
    return;
  }

  const normalized = normalizeSubmitContext(context);
  if (!normalized) {
    window.sessionStorage.removeItem(SUBMIT_CONTEXT_STORAGE_KEY);
    return;
  }

  window.sessionStorage.setItem(
    SUBMIT_CONTEXT_STORAGE_KEY,
    JSON.stringify(normalized),
  );
}

export function readSubmitContext(): SubmitContext | null {
  if (typeof window === "undefined") {
    return null;
  }

  const rawValue = window.sessionStorage.getItem(SUBMIT_CONTEXT_STORAGE_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as SubmitContext;
    return normalizeSubmitContext(parsed);
  } catch {
    window.sessionStorage.removeItem(SUBMIT_CONTEXT_STORAGE_KEY);
    return null;
  }
}

export function clearSubmitContext() {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(SUBMIT_CONTEXT_STORAGE_KEY);
}
